import React, { UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { AIAgent, PortalStyleConfig, ThemeColorSchemas, UnleashFeatures } from '@nx-frontend/shared/types';
import { AgentModeForm } from './agent-mode-form';
import { $agentsHistory, $aiHelperAgent } from './agent-store';
import { ChatbotMessageComponent } from './chatbot-message';
import { ChatbotPromptComponent } from './chatbot-prompt';
import { $aiThreads, $writingMessage, setEditMessageIdEvent } from './store';
import { AgentInput, AgentThreadTriggeredBy, ChatbotMessage, ChatbotMessageRole } from './types';
import { useAgentGenerateResponse } from './use-agent-generate-response';

interface ChatbotChatProps {
  threadId: string | null;
  onSendMessage: (value: string) => void;
  aiName: string;
  aiCustomLogoUrl: string | null;
  isWriting: boolean;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onAddMessageToPage: (text: string, threadId: string) => void;
  onFeedback: (threadId: string, message: ChatbotMessage) => void;
  allowCreatePage?: boolean;
  disableLoading?: boolean;
  isAgent?: boolean;
  /**
   * whether to scroll to the bottom continously when AI is writing
   */
  enableAutoScroll?: boolean;
  /**
   * Whether to scroll to the writing assistant initially, by default false
   */
  initialScrollToAssistantWriting?: boolean;
  /**
   * Whether to scroll to the user message when it is added, imitates ChatGPT behavior
   */
  scrollToUserMessage?: boolean;

  animateIn?: boolean;

  className?: string[] | string;
}

export function ChatbotChat({
  threadId,
  onSendMessage,
  themeColors,
  themeStyle,
  aiName,
  aiCustomLogoUrl,
  isWriting,
  onAddMessageToPage,
  onFeedback,
  allowCreatePage = true,
  disableLoading,
  isAgent,
  enableAutoScroll = true,
  initialScrollToAssistantWriting = false,
  scrollToUserMessage = false,
  animateIn = false,
  className = [],
}: ChatbotChatProps) {
  const [aiThreads, writingMessage, unleashFlags, agentStore, agentsHistory] = useUnit([
    $aiThreads,
    $writingMessage,
    $unleashFlags,
    $aiHelperAgent,
    $agentsHistory,
  ]);
  const [startEditMessage] = useEvent([setEditMessageIdEvent]);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [autoScrollOnWriting, setAutoScrollOnWriting] = useState(true);
  const writingMessageRef = useRef<HTMLDivElement>(null);
  const agentFormContainerRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const scrolledToAssistantInitially = useRef(false);
  const lastUserMessageScrolledToRef = useRef<ChatbotMessage | null>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [creatingThreadInProgress, setCreatingThreadInProgress] = useState(false);

  let agent: AIAgent | undefined = undefined;
  if (isAgent && agentStore.state === 'loaded') {
    agent = agentStore.agent as AIAgent;
  }

  const agentThread = useMemo(() => {
    return agent && threadId
      ? agentsHistory[agent.globalId]?.threads?.find((aThread) => aThread.id === threadId)
      : undefined;
  }, [agent, threadId, agentsHistory]);

  const thread = threadId ? aiThreads.threads[threadId] : undefined;

  const displayMessageContext = unleashFlags[UnleashFeatures.AI_ASSISTANT_DETECT_USER_MESSAGE_CONTEXT];

  const messages = useMemo(() => {
    return threadId && aiThreads.threads?.[threadId]
      ? aiThreads.threads?.[threadId].messages || []
      : aiThreads.tmpMessages || [];
  }, [aiThreads.threads, aiThreads.tmpMessages, threadId]);
  const [loading, setLoading] = useState(!disableLoading && !messages.length);

  useEffect(() => {
    if (
      scrollToUserMessage &&
      lastUserMessageRef.current &&
      messages.length > 0 &&
      messages[messages.length - 1].role === ChatbotMessageRole.USER &&
      lastUserMessageScrolledToRef.current !== messages[messages.length - 1]
    ) {
      lastUserMessageScrolledToRef.current = messages[messages.length - 1];
      const node = lastUserMessageRef.current;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages.length, messages, scrollToUserMessage]);

  const aiWritingMessage = useMemo(() => {
    if (writingMessage !== null) {
      const { threadId: writingMessageThreadId, ...message } = writingMessage;
      if (!threadId || !writingMessageThreadId || writingMessageThreadId === threadId) {
        return message;
      }
    }
    return false;
  }, [threadId, writingMessage]);

  const t = useTranslations('features');

  const suggestions = threadId && aiThreads.threads?.[threadId] ? aiThreads.threads?.[threadId].suggestions : undefined;

  const scrollToBottom = useCallback(({ behavior }: { behavior?: 'smooth' } = {}) => {
    if (messagesRef.current !== null) {
      const node = messagesRef.current;
      node.scrollTo({
        top: node.scrollHeight,
        behavior,
      });
    }
  }, []);

  const onScrollMessages = useCallback(
    (event: UIEvent) => {
      const target = event.target as HTMLDivElement;
      if (isWriting) {
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
          if (!autoScrollOnWriting) {
            setAutoScrollOnWriting(true);
          }
        } else {
          if (autoScrollOnWriting) {
            setAutoScrollOnWriting(false);
          }
        }
      }
    },
    [autoScrollOnWriting, isWriting]
  );

  /* Scroll to end of chat on mount, load messages, add new messages */
  useEffect(() => {
    if (loading && messages.length) {
      setLoading(false);
    }
  }, [messages.length, loading]);

  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [scrollToBottom, loading]);

  /* Set autoscroll flag on start AI writing */
  useEffect(() => {
    if (isWriting) {
      setAutoScrollOnWriting(true);
    } else {
      setAutoScrollOnWriting(false);
    }
  }, [isWriting]);

  /* On AI writing scroll */
  useEffect(() => {
    if (autoScrollOnWriting && isWriting && enableAutoScroll) {
      scrollToBottom();
    }
  }, [autoScrollOnWriting, scrollToBottom, aiWritingMessage, isWriting, enableAutoScroll]);

  /* On end AI writing scroll to bottom if available */
  useEffect(() => {
    if (autoScrollOnWriting && !isWriting && enableAutoScroll) {
      scrollToBottom({ behavior: 'smooth' });
    }
  }, [autoScrollOnWriting, isWriting, scrollToBottom, enableAutoScroll]);

  const onAddToPage = useCallback(
    (text: string) => {
      if (threadId) {
        onAddMessageToPage(text, threadId);
      }
    },
    [threadId, onAddMessageToPage]
  );

  const onMessageFeedback = useCallback(
    (message: ChatbotMessage) => {
      if (threadId) {
        onFeedback(threadId, message);
      }
    },
    [onFeedback, threadId]
  );

  const onStartEditMessage = (message: ChatbotMessage) => {
    if (message.id && threadId) {
      startEditMessage({ threadId, messageId: message.id });
    }
  };

  const onAgentGenerateResponse = useAgentGenerateResponse({
    agent,
    setCreatingThreadInProgress,
  });

  const onFormSubmit = useCallback(
    (input: AgentInput) => {
      onAgentGenerateResponse(input);
    },
    [onAgentGenerateResponse]
  );

  useEffect(() => {
    if (!messagesRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      setSpacerHeight(messagesRef.current?.clientHeight || 0);
    });
    resizeObserver.observe(messagesRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (
      initialScrollToAssistantWriting &&
      isWriting &&
      !scrolledToAssistantInitially.current &&
      writingMessageRef.current &&
      spacerHeight > 0
    ) {
      const node = writingMessageRef.current;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrolledToAssistantInitially.current = true;
    }
  }, [isWriting, initialScrollToAssistantWriting, spacerHeight]);

  const lastMessageContainerMinHeight = useMemo(() => {
    if (enableAutoScroll) {
      return 0;
    }
    return spacerHeight;
  }, [enableAutoScroll, spacerHeight]);

  const agentThreadTriggeredByAutomation = useMemo(() => {
    if (agentThread && agentThread.triggeredBy) {
      return agentThread.triggeredBy === AgentThreadTriggeredBy.AUTOMATION;
    }
    return false;
  }, [agentThread]);

  // Define transition states and styles
  const transitionStyles: Record<TransitionStatus, string> = {
    entering: 'opacity-0',
    entered: 'opacity-100',
    exiting: 'opacity-100',
    exited: 'opacity-0',
    unmounted: 'opacity-0',
  };

  return (
    <Transition in={true} timeout={animateIn ? 200 : 0} appear>
      {(state) => (
        <div
          ref={messagesRef}
          className={classNames(
            `overflow-y-auto grow py-6 overflow-x-hidden transition-opacity duration-300 ease-in-out`,
            'pl-2 [scrollbar-gutter:stable]', // needed to ensure messages are always centered in the outer container
            transitionStyles[state],
            className
          )}
          onScroll={onScrollMessages}
        >
          {loading ? (
            <div className="flex items-center justify-center w-full h-full">
              <Spinner width={42} height={42} />
            </div>
          ) : (
            <>
              {isAgent && agent && agent.inputs.length > 0 && thread && !agentThreadTriggeredByAutomation && (
                <div className="mb-8 mt-8" ref={agentFormContainerRef}>
                  <AgentModeForm
                    agent={agent}
                    onSubmit={onFormSubmit}
                    defaults={thread.input}
                    creatingThreadInProgress={creatingThreadInProgress}
                  />
                </div>
              )}
              <div className="flex flex-col gap-9 max-w-2xl px-5 mx-auto">
                {messages.map((message, index) => (
                  <div
                    ref={
                      index === messages.length - 1 && message.role === ChatbotMessageRole.USER
                        ? lastUserMessageRef
                        : undefined
                    }
                    key={'message-' + threadId + '-' + index + (message.id ?? '')}
                    style={
                      !aiWritingMessage && index === messages.length - 1
                        ? {
                            minHeight: lastMessageContainerMinHeight + 'px',
                          }
                        : {}
                    }
                  >
                    <ChatbotMessageComponent
                      displayMessageContext={displayMessageContext}
                      message={message}
                      themeColors={themeColors}
                      themeStyle={themeStyle}
                      isWriting={index === messages.length - 1 && isWriting}
                      aiName={aiName}
                      aiCustomLogoUrl={aiCustomLogoUrl}
                      isLastMessage={!writingMessage && index === messages.length - 1}
                      onAddMessageToPage={onAddToPage}
                      onFeedback={onMessageFeedback}
                      allowCreatePage={allowCreatePage}
                      disliked={Boolean(
                        threadId && message.id && aiThreads.threads[threadId].dislikeMessages?.includes(message.id)
                      )}
                      onStartEditMessage={onStartEditMessage}
                      isAgent={isAgent}
                    />
                  </div>
                ))}
                {aiWritingMessage ? (
                  <div ref={writingMessageRef} style={{ minHeight: lastMessageContainerMinHeight + 'px' }}>
                    <ChatbotMessageComponent
                      key={'message-' + threadId + '-writing-message'}
                      message={aiWritingMessage}
                      themeColors={themeColors}
                      themeStyle={themeStyle}
                      isWriting={true}
                      aiName={aiName}
                      aiCustomLogoUrl={aiCustomLogoUrl}
                      isLastMessage={true}
                      isAgent={isAgent}
                    />
                  </div>
                ) : null}
              </div>
              {!isWriting && suggestions && suggestions.length ? (
                <div className="flex flex-col w-full gap-0 mt-0 pt-2" ref={suggestionsRef}>
                  <div className="mx-auto w-full max-w-2xl px-5">
                    <h2 className="font-semibold text-sm mb-1 mt-0">{t('ai-helper.related_for_you')}</h2>
                    <div className="chatbot-suggestions flex flex-col w-full gap-1">
                      {suggestions.slice(0, 5).map((suggestion, index) => (
                        <ChatbotPromptComponent
                          key={'suggestion-' + threadId + '-' + index}
                          text={suggestion}
                          onClick={onSendMessage}
                          promptType="chatSuggestion"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </Transition>
  );
}
